import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getUserBySubsidiaries } from "./userHelpers";

/**
 * Obtém todos os módulos acessíveis para as subsidiárias fornecidas
 * @param {Array<string>} userSubsidiaries - Array de IDs/nomes das subsidiárias do usuário
 * @returns {Promise<Array>} Array de referências de módulos (DocumentReference)
 */
export const getModulesForUserSubsidiaries = async (userSubsidiaries) => {
  try {
    const modulesRef = collection(db, "modules");

    // Obtém todos os módulos visíveis para todos
    const visibleQuery = query(
      modulesRef,
      where("isVisibleForAll", "==", true)
    );
    const visibleSnapshot = await getDocs(visibleQuery);
    const moduleRefs = visibleSnapshot.docs.map((doc) => doc.ref);

    // Se não há subsidiárias, retorna apenas módulos visíveis para todos
    if (!userSubsidiaries || userSubsidiaries.length === 0) {
      return moduleRefs;
    }

    // Obtém TODOS os módulos para verificar subsidiárias
    const allModulesSnapshot = await getDocs(collection(db, "modules"));

    for (const moduleDoc of allModulesSnapshot.docs) {
      const moduleData = moduleDoc.data();

      // Pula se já foi adicionado (visível para todos)
      if (moduleData.isVisibleForAll) {
        continue;
      }

      // Verifica se alguma subsidiária do usuário está na lista do módulo
      if (moduleData.subsidiaries && Array.isArray(moduleData.subsidiaries)) {
        const hasMatchingSubsidiary = userSubsidiaries.some((userSub) =>
          moduleData.subsidiaries.includes(userSub)
        );

        if (hasMatchingSubsidiary) {
          // Adiciona se ainda não estiver na lista
          if (!moduleRefs.some((ref) => ref.id === moduleDoc.id)) {
            moduleRefs.push(moduleDoc.ref);
          }
        }
      }
    }

    return moduleRefs;
  } catch (error) {
    console.error(
      "[getModulesForUserSubsidiaries] Erro ao obter módulos:",
      error
    );
    return [];
  }
};

/**
 * Sincroniza os módulos de um usuário com base em suas subsidiárias
 * Útil para garantir que usuários tenham acesso aos módulos corretos
 * IMPORTANTE: Funciona tanto para domínios corporativos quanto para emails genéricos (Gmail, etc)
 * @param {string} email - Email do usuário
 * @param {string} domain - Domínio do email do usuário (ex: 'company.com', 'gmail.com')
 * @param {Array<string>} subsidiaries - Subsidiárias do usuário
 * @returns {Promise<boolean>} True se atualizado com sucesso, false se não houve mudanças
 */
export const syncUserModules = async (email, domain, subsidiaries) => {
  try {
    // Tenta buscar no domínio primeiro
    let userDomainRef = doc(db, "regional_enterprises", domain);
    let userDomainDoc = await getDoc(userDomainRef);
    let usersMap = null;
    let userDoc = null;

    if (userDomainDoc.exists()) {
      usersMap = userDomainDoc.data().users || {};
      userDoc = usersMap[email];
    }

    // Se não encontrou no domínio, busca em 'other_users' (emails genéricos)
    if (!userDoc) {
      userDomainRef = doc(db, "regional_enterprises", "other_users");
      userDomainDoc = await getDoc(userDomainRef);

      if (!userDomainDoc.exists()) {
        console.warn(
          `[syncUserModules] Documento 'other_users' não encontrado`
        );
        return false;
      }

      usersMap = userDomainDoc.data().users || {};
      userDoc = usersMap[email];
    }

    if (!userDoc) {
      console.warn(
        `[syncUserModules] Usuário ${email} não encontrado em nenhum domínio`
      );
      return false;
    }

    // Busca TODOS os módulos para calcular acesso do zero
    const allModulesSnapshot = await getDocs(collection(db, "modules"));
    const accessibleModules = [];

    for (const moduleDoc of allModulesSnapshot.docs) {
      const moduleData = moduleDoc.data();

      // 1. Verifica visibleForAll
      // Se true, todos usuários têm acesso automaticamente
      if (moduleData.isVisibleForAll) {
        accessibleModules.push(moduleDoc.ref);
        continue;
      }

      // 2. Verifica acesso por subsidiária
      // Compara subsidiárias do usuário com subsidiárias permitidas no módulo
      const moduleSubsidiaries = moduleData.subsidiaries || [];
      const hasSubsidiaryAccess = (subsidiaries || []).some((userSub) =>
        moduleSubsidiaries.includes(userSub)
      );

      if (hasSubsidiaryAccess) {
        accessibleModules.push(moduleDoc.ref);
        continue;
      }

      // 3. Verifica acesso manual (allowedUsers)
      const allowedUsers = moduleData.allowedUsers || [];
      if (allowedUsers.includes(email)) {
        accessibleModules.push(moduleDoc.ref);
      }
    }

    // Compara módulos atuais com calculados
    // Só atualiza se houver diferença (evita writes desnecessários)
    const currentModules = userDoc.accessibleModules || [];
    const currentModuleIds = currentModules.map((ref) => ref.id).sort();
    const newModuleIds = accessibleModules.map((ref) => ref.id).sort();

    if (JSON.stringify(currentModuleIds) !== JSON.stringify(newModuleIds)) {
      // Atualiza accessibleModules do usuário no Firestore
      usersMap[email].accessibleModules = accessibleModules;
      await updateDoc(userDomainRef, { users: usersMap });
      return true;
    }

    return false;
  } catch (error) {
    console.error("[syncUserModules] Erro ao sincronizar módulos:", error);
    return false;
  }
};

/**
 * Separa usuários de um módulo em manuais e por subsidiária
 * @param {Array} allUsers - Todos os usuários com acesso
 * @param {Object} module - Dados do módulo
 * @returns {Object} { manualUsers, subsidiaries }
 */
export const categorizeModuleUsers = (allUsers, module) => {
  const allowedUsers = module.allowedUsers || [];

  const manualUsers = allUsers.filter((user) =>
    allowedUsers.includes(user.email)
  );

  return { manualUsers, subsidiaries: module.subsidiaries || [] };
};

/**
 * Conta quantos usuários ATIVOS existem em cada subsidiária
 * Busca em TODOS os domínios (regional_enterprises incluindo domínios corporativos e other_users)
 * @param {Array<string>} subsidiaryIds - IDs das subsidiárias
 * @returns {Promise<Object>} { subsidiaryId: count }
 */
export const countActiveUsersBySubsidiaries = async (subsidiaryIds) => {
  const counts = {};

  try {
    // Busca todos os documentos de regional_enterprises
    const enterprisesSnapshot = await getDocs(
      collection(db, "regional_enterprises")
    );

    // Para cada subsidiária, conta usuários ativos
    for (const subId of subsidiaryIds) {
      let count = 0;

      enterprisesSnapshot.forEach((docSnapshot) => {
        const usersMap = docSnapshot.data().users || {};

        // Conta usuários ativos que pertencem a esta subsidiária
        Object.values(usersMap).forEach((user) => {
          if (
            user.isActive !== false && // Conta como ativo se isActive não for explicitamente false
            user.subsidiaries &&
            user.subsidiaries.includes(subId)
          ) {
            count++;
          }
        });
      });

      counts[subId] = count;
    }
  } catch (error) {
    console.error("[countActiveUsersBySubsidiaries] Erro:", error);
  }

  return counts;
};

/**
 * Conta quantos usuários existem em cada subsidiária (MANTIDO PARA COMPATIBILIDADE)
 * Busca apenas em other_users
 * @param {Array<string>} subsidiaryIds - IDs das subsidiárias
 * @returns {Promise<Object>} { subsidiaryId: count }
 */
export const countUsersBySubsidiaries = async (subsidiaryIds) => {
  const counts = {};

  for (const subId of subsidiaryIds) {
    const users = await getUserBySubsidiaries([subId]);
    counts[subId] = users.length;
  }

  return counts;
};
